// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Message, List, Button, Icon, Popup } from 'semantic-ui-react';
import { Brokerage } from '../../api/brokerageApi';

export type ProviderAlias = 'microsoft' | 'google';

interface Props {
  brokerage: Brokerage;
  provider: ProviderAlias;
}

/**
 * Surfaces the exact redirect URIs the brokerage IT team must register on
 * their OAuth app. The strings are deterministic from the brokerage's
 * subdomain — we compute and display them so the platform admin can
 * forward them to brokerage IT verbatim.
 *
 * Phase 1 / 2: the Phase-1 SSO callback differs by provider (Keycloak's
 * /broker/<alias>/endpoint). The Phase-2+ communications-service callback
 * (/api/integrations/<alias>/callback) is reserved for Phase 3+ for
 * Microsoft, Phase 7 for Google — registering both URIs now means
 * brokerage IT doesn't have to come back when later phases activate.
 *
 * The base domain is read from a build-time env var; falls back to
 * "advisor.localhost.com" for local dev.
 */
function RedirectUriHelper({ brokerage, provider }: Props) {
  const baseDomain =
    (import.meta.env?.VITE_ADVISOR_BASE_DOMAIN as string | undefined) ?? 'advisor.localhost.com';
  const subdomain = brokerage.subdomain;
  // Use the actual Keycloak realm name resource-server stored on the
  // brokerage row — NOT a hardcoded `brokerage-{subdomain}` template.
  // In some envs the realm is named exactly after the subdomain ("gtpf"),
  // in others it's prefixed ("brokerage-gtpf"). Mismatch here means Google
  // / Microsoft return redirect_uri_mismatch when the advisor tries SSO,
  // because brokerage IT registered the wrong URL.
  const realm = brokerage.keycloakRealmId && brokerage.keycloakRealmId.trim().length > 0
    ? brokerage.keycloakRealmId
    : `brokerage-${subdomain}`;

  // Per CLAUDE.md §17–§18: Keycloak is served from each tenant's own
  // subdomain via the NGINX /auth/ proxy. There is NO separate
  // auth.{subdomain} subdomain. The realm's frontendUrl is
  // https://{subdomain}.advisor.{baseDomain}/auth — and the broker
  // callback path lives at that same origin.
  const ssoCallback =
    `https://${subdomain}.${baseDomain}/auth/realms/${realm}/broker/${provider}/endpoint`;
  const integrationCallback =
    `https://${subdomain}.${baseDomain}/api/integrations/${provider}/callback`;

  const providerLabel = provider === 'microsoft' ? 'Entra app' : 'OAuth client';
  const futurePhaseLabel = provider === 'microsoft' ? 'Phase 3+' : 'Phase 7';

  return (
    <Message>
      <Message.Header>Redirect URIs for brokerage IT</Message.Header>
      <p>Send these to the brokerage IT team to register on their {providerLabel}:</p>
      <List divided relaxed>
        <UriRow label="SSO callback (Phase 1+)" value={ssoCallback} />
        <UriRow label={`Calendar/email callback (${futurePhaseLabel})`} value={integrationCallback} />
      </List>
    </Message>
  );
}

function UriRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <List.Item>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <strong>{label}</strong>
          <pre style={{ background: '#f4f4f4', padding: 8, borderRadius: 4, fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {value}
          </pre>
        </div>
        <Popup
          open={copied}
          trigger={
            <Button icon size="small" onClick={copy} title="Copy to clipboard">
              <Icon name={copied ? 'check' : 'copy'} />
            </Button>
          }
          content="Copied!"
          position="left center"
        />
      </div>
    </List.Item>
  );
}

export default RedirectUriHelper;
